/**
 * NOTIFICATION-WEB-PUSH-C2 — browser Web Push helpers.
 *
 * Notification.requestPermission() must ONLY run inside explicitEnableWebPush().
 * Never call it on import, render, bootstrap, or reconcile.
 */

import { createDefaultBrowserStorageDriver } from '../persistence/browserStorageDriver';

const GENERIC_TITLE = 'Нове сповіщення в Рієстро';
const GENERIC_BODY = 'Відкрийте застосунок, щоб переглянути деталі.';
const NOTIFICATIONS_PATH = '/notifications';

/**
 * Origin-local opaque browser-instance identity for Web Push continuity.
 * Contains no account/user information. Never sent in notification payloads.
 * Key is stable across enable + authenticated reconcile for this origin.
 */
export const WEB_PUSH_DEVICE_INSTANCE_STORAGE_KEY =
  'riestro.webPush.deviceInstanceId';

function createRandomUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older runtimes — still opaque and non-user-identifying.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function resolveDeviceInstanceStorage(storage) {
  if (storage !== undefined) {
    return storage;
  }
  try {
    // Approved P-1 persistence boundary — no raw browser storage access here.
    return createDefaultBrowserStorageDriver();
  } catch (_err) {
    return null;
  }
}

/**
 * Create or reuse a stable logical browser-instance UUID in origin storage.
 * Default production path uses the approved persistence browser storage driver.
 * Tests may inject a synchronous storage seam via ``storage``.
 */
export function getOrCreateDeviceInstanceId({ storage } = {}) {
  const driver = resolveDeviceInstanceStorage(storage);
  if (!driver || typeof driver.getItem !== 'function') {
    return createRandomUuid();
  }
  try {
    const existing = driver.getItem(WEB_PUSH_DEVICE_INSTANCE_STORAGE_KEY);
    if (typeof existing === 'string' && existing.trim()) {
      return existing.trim();
    }
    const created = createRandomUuid();
    driver.setItem(WEB_PUSH_DEVICE_INSTANCE_STORAGE_KEY, created);
    return created;
  } catch (_err) {
    return createRandomUuid();
  }
}

function buildWebPushIdentityBody(subscription) {
  return {
    subscription: subscriptionToJSON(subscription),
    device_label: buildCoarseDeviceLabel(),
    device_instance_id: getOrCreateDeviceInstanceId(),
  };
}

export const WEB_PUSH_PERMISSION = {
  UNSUPPORTED: 'unsupported',
  UNAVAILABLE: 'unavailable',
  DEFAULT: 'default',
  DENIED: 'denied',
  GRANTED: 'granted',
};

export function isWebPushSupported() {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(
      window.Notification
      && navigator.serviceWorker
      && window.PushManager,
    );
  } catch (_err) {
    return false;
  }
}

export function getNotificationPermissionState() {
  if (!isWebPushSupported()) {
    return WEB_PUSH_PERMISSION.UNSUPPORTED;
  }
  const permission = window.Notification.permission;
  if (permission === 'granted') return WEB_PUSH_PERMISSION.GRANTED;
  if (permission === 'denied') return WEB_PUSH_PERMISSION.DENIED;
  if (permission === 'default') return WEB_PUSH_PERMISSION.DEFAULT;
  return WEB_PUSH_PERMISSION.UNSUPPORTED;
}

/**
 * Convert a URL-safe base64 VAPID public key to a Uint8Array.
 */
export function urlBase64ToUint8Array(base64String) {
  if (typeof base64String !== 'string' || !base64String.trim()) {
    throw new Error('Invalid VAPID public key');
  }
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  let rawData;
  try {
    rawData = window.atob(base64);
  } catch (_err) {
    throw new Error('Invalid VAPID public key');
  }
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  // Uncompressed P-256 public point must be exactly 65 bytes starting with 0x04.
  if (output.length !== 65 || output[0] !== 0x04) {
    throw new Error('Invalid VAPID public key');
  }
  return output;
}

export const WEB_PUSH_LOCAL_SUBSCRIPTION = {
  UNSUPPORTED: 'unsupported',
  UNAVAILABLE: 'unavailable',
  CHECKING: 'checking',
  PRESENT: 'present',
  MISSING: 'missing',
};

/** Bounded wait when a registration exists but is not yet active. */
export const WEB_PUSH_SERVICE_WORKER_READY_TIMEOUT_MS = 3000;

/**
 * Pure local inspection — never prompts, never subscribes, never calls backend.
 */
export async function inspectCurrentWebPushSubscription() {
  if (!isWebPushSupported()) {
    return WEB_PUSH_LOCAL_SUBSCRIPTION.UNSUPPORTED;
  }
  try {
    const registration = await getReadyRegistration();
    if (!registration || !registration.pushManager) {
      return WEB_PUSH_LOCAL_SUBSCRIPTION.UNAVAILABLE;
    }
    const subscription = await registration.pushManager.getSubscription();
    return subscription
      ? WEB_PUSH_LOCAL_SUBSCRIPTION.PRESENT
      : WEB_PUSH_LOCAL_SUBSCRIPTION.MISSING;
  } catch (_err) {
    return WEB_PUSH_LOCAL_SUBSCRIPTION.UNAVAILABLE;
  }
}

export function buildCoarseDeviceLabel(userAgent = '') {
  const ua = String(userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '');
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';

  let platform = 'Desktop';
  if (/iPhone|iPad|iPod/.test(ua)) platform = 'iOS';
  else if (/Android/.test(ua)) platform = 'Android';
  else if (/Windows/.test(ua)) platform = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) platform = 'macOS';
  else if (/Linux/.test(ua)) platform = 'Linux';

  return `${browser} · ${platform}`;
}

export function subscriptionToJSON(subscription) {
  if (!subscription || typeof subscription.toJSON !== 'function') {
    return null;
  }
  return subscription.toJSON();
}

/**
 * Bounded current-scope registration acquisition.
 * Never registers a service worker. Never waits unboundedly on ready.
 */
async function getReadyRegistration() {
  if (!isWebPushSupported()) return null;
  const serviceWorker = navigator.serviceWorker;
  if (!serviceWorker || typeof serviceWorker.getRegistration !== 'function') {
    return null;
  }

  let registration = null;
  try {
    registration = await serviceWorker.getRegistration();
  } catch (_err) {
    return null;
  }

  if (!registration) {
    return null;
  }

  if (registration.active) {
    return registration;
  }

  // Registration exists but is not active yet — short bounded ready wait.
  return waitForServiceWorkerReady(serviceWorker, WEB_PUSH_SERVICE_WORKER_READY_TIMEOUT_MS);
}

function waitForServiceWorkerReady(serviceWorker, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    let timerId = null;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      resolve(value || null);
    };

    timerId = setTimeout(() => finish(null), timeoutMs);

    Promise.resolve(serviceWorker.ready).then(
      (reg) => finish(reg || null),
      () => finish(null),
    );
  });
}

/**
 * Explicit enable path — the ONLY place that may call requestPermission().
 */
export async function explicitEnableWebPush({
  publicKey,
  enableOnServer,
  requestPermission = null,
}) {
  if (!isWebPushSupported()) {
    throw Object.assign(new Error('unsupported'), { code: 'unsupported' });
  }
  if (!publicKey) {
    throw Object.assign(new Error('unavailable'), { code: 'unavailable' });
  }

  const permissionFn = requestPermission
    || (() => window.Notification.requestPermission());
  const permission = await permissionFn();
  if (permission !== 'granted') {
    throw Object.assign(new Error('permission_denied'), {
      code: permission === 'denied' ? 'denied' : 'default',
    });
  }

  const registration = await getReadyRegistration();
  if (!registration || !registration.pushManager) {
    throw Object.assign(new Error('unavailable'), { code: 'unavailable' });
  }

  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  const body = buildWebPushIdentityBody(subscription);

  try {
    return await enableOnServer(body);
  } catch (err) {
    const status = err && err.response && err.response.status;
    const code = err && err.response && err.response.data && err.response.data.code;
    // One-time recovery only for exact cross-user endpoint conflict.
    if (status === 409 && code === 'web_push_endpoint_conflict') {
      try {
        await subscription.unsubscribe();
      } catch (_unsubErr) {
        // continue to resubscribe attempt
      }
      const fresh = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      return enableOnServer(buildWebPushIdentityBody(fresh));
    }
    throw err;
  }
}

export const WEB_PUSH_RECOVERY_REASON = {
  SUBSCRIPTION_REVOKED: 'subscription_revoked',
  ENDPOINT_CONFLICT: 'endpoint_conflict',
  SUBSCRIPTION_MISSING: 'subscription_missing',
};

/**
 * Authenticated reconcile — never prompts, never calls subscribe()/unsubscribe().
 */
export async function reconcileWebPushIfEligible({
  isAuthenticated,
  serverWebPushEnabled,
  reconcileOnServer,
}) {
  if (!isAuthenticated) return { status: 'skipped_logged_out' };
  if (!isWebPushSupported()) return { status: 'skipped_unsupported' };
  if (getNotificationPermissionState() !== WEB_PUSH_PERMISSION.GRANTED) {
    return { status: 'skipped_permission' };
  }
  if (!serverWebPushEnabled) return { status: 'skipped_server_disabled' };

  const registration = await getReadyRegistration();
  if (!registration || !registration.pushManager) {
    return { status: 'skipped_no_registration' };
  }
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return {
      status: 'needs_recovery',
      reason: WEB_PUSH_RECOVERY_REASON.SUBSCRIPTION_MISSING,
    };
  }

  try {
    const result = await reconcileOnServer(buildWebPushIdentityBody(subscription));
    return { status: 'reconciled', result };
  } catch (err) {
    const status = err && err.response && err.response.status;
    const code = err && err.response && err.response.data && err.response.data.code;
    if (status === 409 && code === 'web_push_subscription_revoked') {
      return {
        status: 'needs_recovery',
        reason: WEB_PUSH_RECOVERY_REASON.SUBSCRIPTION_REVOKED,
      };
    }
    if (status === 409 && code === 'web_push_endpoint_conflict') {
      return {
        status: 'needs_recovery',
        reason: WEB_PUSH_RECOVERY_REASON.ENDPOINT_CONFLICT,
      };
    }
    return { status: 'failed' };
  }
}

export async function disableWebPushLocally({ disableOnServer }) {
  const serverResult = await disableOnServer();
  let localUnsubscribed = false;
  let localError = null;
  try {
    if (isWebPushSupported()) {
      const registration = await getReadyRegistration();
      const subscription = registration
        ? await registration.pushManager.getSubscription()
        : null;
      if (subscription) {
        localUnsubscribed = await subscription.unsubscribe();
      }
    }
  } catch (err) {
    localError = err;
  }
  return {
    serverResult,
    localUnsubscribed,
    localError,
    permission: getNotificationPermissionState(),
  };
}

export const WEB_PUSH_COPY = {
  GENERIC_TITLE,
  GENERIC_BODY,
  NOTIFICATIONS_PATH,
};
